import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import ConfirmationModal from '../ConfirmationModal';

// A small component to display a single card
const CardDisplay = ({ card, onRemoveClick }) => {
    return (
        <div className="flex justify-between items-center p-4 border border-slate-200 dark:border-brand-gray/50 rounded-lg bg-gray-50 dark:bg-brand-black/50 transition-colors">
            <div>
                <p className="font-semibold capitalize dark:text-brand-white">{card.brand} **** {card.last4}</p>
                <p className="text-sm text-gray-500 dark:text-brand-gray">Expires {String(card.exp_month).padStart(2, '0')}/{card.exp_year}</p>
            </div>
            <button onClick={() => onRemoveClick(card)} className="text-sm text-red-600 hover:underline">
                Remove
            </button>
        </div>
    );
};


// The main settings component
const PaymentDetailsSettings = () => {
    const [cards, setCards] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [cardToDelete, setCardToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchCards = useCallback(async () => {
        setIsLoading(true);
        try {
            const functions = getFunctions();
            const getSavedPaymentMethods = httpsCallable(functions, 'getSavedPaymentMethods');
            const result = await getSavedPaymentMethods();
            setCards(result.data);
        } catch (error) {
            toast.error(`Could not load payment methods: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCards();
    }, [fetchCards]);

    const handleCardDeleted = (deletedCardId) => {
        setCards(prevCards => prevCards.filter(card => card.id !== deletedCardId));
    };


    const handleOpenConfirmModal = (card) => {
        setCardToDelete(card);
        setIsConfirmModalOpen(true);
    };

    const handleCloseConfirmModal = () => {
        setCardToDelete(null);
        setIsConfirmModalOpen(false);
    };

    const handleConfirmDelete = async () => {
        if (!cardToDelete) return;
        
        setIsDeleting(true);
        try {
            const functions = getFunctions();
            const deletePaymentMethod = httpsCallable(functions, 'deletePaymentMethod');
            await deletePaymentMethod({ paymentMethodId: cardToDelete.id });
            
            toast.success("Card removed successfully.");
            setCards(prevCards => prevCards.filter(card => card.id !== cardToDelete.id)); // Update UI immediately
        } catch (error) {
            toast.error(`Failed to remove card: ${error.message}`);
        } finally {
            setIsDeleting(false);
            handleCloseConfirmModal();
        }
    };

return (
        <div className="p-6 bg-white dark:bg-brand-gray/10 rounded-3xl shadow-md border border-transparent dark:border-brand-gray/70 transition-colors">
            <h2 className="text-xl font-bold mb-4 dark:text-brand-white">Your Saved Cards</h2>
            {isLoading ? (
                <p className="dark:text-brand-gray">Loading payment details...</p>
            ) : cards.length > 0 ? (
                <div className="space-y-4">
                    {cards.map(card => (
                        <CardDisplay key={card.id} card={card} onRemoveClick={handleOpenConfirmModal} />
                    ))}
                </div>
            ) : (
                <p className="text-gray-600 dark:text-brand-gray">You have no saved cards on file.</p>
            )}

            <Link to="/add-payment-method" className="inline-block mt-6 bg-brand-pink text-white font-semibold px-6 py-2 rounded-lg hover:brightness-110 active:brightness-90 transition-all">
                + Add New Card
            </Link>

            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                onClose={handleCloseConfirmModal}
                onConfirm={handleConfirmDelete}
                title="Remove Payment Method"
                message={`Are you sure you want to remove the card ending in ${cardToDelete?.last4}? This action cannot be undone.`}
                confirmText="Remove Card"
                confirmColor="bg-red-600"
                isProcessing={isDeleting}
            />
        </div>
    );
};

export default PaymentDetailsSettings;